import { useAuthContext } from '@shared/auth';
import { useMe } from '@store/entities/me/hooks';
import { useGetMePermissions } from '@store/entities/mePermission';
import { useCallback, useMemo } from 'react';

export const usePermissionCheck = () => {
  const { authenticated, authenticating } = useAuthContext();
  const { me } = useMe();
  const { permissions } = useGetMePermissions();

  const permissionSlugs = useMemo(
    () => permissions.map((permission) => permission.slug).filter(Boolean),
    [permissions]
  );

  const permissionSet = useMemo(() => new Set(permissionSlugs), [permissionSlugs]);

  const hasAll = useCallback(
    (required: string[] = []) => required.every((slug) => permissionSet.has(slug)),
    [permissionSet]
  );

  const hasAny = useCallback(
    (required: string[] = []) =>
      required.length === 0 || required.some((slug) => permissionSet.has(slug)),
    [permissionSet]
  );

  // Permissions hydrate into the store from the `me` response; an authenticated
  // user whose `me` hasn't landed yet is still loading, not permission-less.
  // Once permissions exist, keep using them (no spinner on mid-session refetches).
  const isChecking = (authenticating || (authenticated && !me)) && permissions.length === 0;

  return {
    hasAll,
    hasAny,
    isChecking,
    permissionSlugs,
    permissions,
  };
};
